import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tecnicos, clusters, tipoOs, clustersBairros, cep as cepApi, geocode } from '../lib/api.js';
import Avatar from '../components/Avatar.jsx';
import UploadFotoModal from '../components/UploadFotoModal.jsx';
import HorarioSemanalEditor from '../components/HorarioSemanalEditor.jsx';

const DIAS_VALUES = [1,2,3,4,5,6,7];
const DIAS_NOMES = ['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'];

export default function TecnicoEditarPage() {
  const { id } = useParams();
  const [mostrarModalFoto, setMostrarModalFoto] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [geocodando, setGeocodando] = useState(false);
  const navigate = useNavigate();
  const [tec, setTec] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [clustersList, setClustersList] = useState([]);
  const [skillsSelecionadas, setSkillsSelecionadas] = useState(new Set());
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => { carregar(); }, [id]);

  async function carregar() {
    setCarregando(true);
    try {
      const [t, top, cs] = await Promise.all([
        tecnicos.get(id),
        tipoOs.list(),
        clusters.list(),
      ]);
      setTec(t.data);
      setTipos(top.data || []);
      setClustersList(cs.data || []);
      const ids = new Set((t.data.skills || []).filter(s => s.ativo).map(s => Number(s.tipo_os_parametro_id)));
      setSkillsSelecionadas(ids);
      setErro(null);
    } catch (e) {
      setErro(e.uiMessage || 'Erro ao carregar técnico');
    } finally { setCarregando(false); }
  }

  function setCampo(k, v) {
    setTec(prev => ({ ...prev, [k]: v }));
    setSucesso(false);
  }

  function toggleDia(dia) {
    const atual = new Set(tec.dias_trabalho || []);
    if (atual.has(dia)) atual.delete(dia); else atual.add(dia);
    setCampo('dias_trabalho', [...atual].sort());
  }

  function toggleSkill(tipoId) {
    // bigint do Postgres chega como string no JS — força Number p/ consistência
    // (sem isso o Set mistura 42 e '42', e o payload falha na validação Zod)
    const id = Number(tipoId);
    setSkillsSelecionadas(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
    setSucesso(false);
  }

  function marcarTodasSkills() {
    setSkillsSelecionadas(new Set(tiposAtivos.map(t => Number(t.id))));
    setSucesso(false);
  }
  function desmarcarTodasSkills() {
    setSkillsSelecionadas(new Set());
    setSucesso(false);
  }

  // Auto-preenche bairros baseado na unidade
  async function preencherBairrosDaUnidade(clusterId) {
    if (!clusterId) return;
    try {
      const r = await clustersBairros(clusterId);
      const bairros = (r.data || []).map(b => b.bairro);
      // Remove duplicatas
      const unicos = [...new Set(bairros)];
      setCampo('bairros_cobertura', unicos);
    } catch (e) {
      console.error('Erro buscando bairros:', e);
    }
  }

  // Quando muda unidade, opcionalmente sugere preencher bairros
  function onMudarUnidade(unidade) {
    const cl = clustersList.find(c => c.unidade_negocio === unidade);
    setCampo('unidade_negocio', unidade || null);
    setCampo('cluster_principal_id', cl?.id || null);
    // Se selecionou unidade real (não "atende todas") e bairros está vazio ou é pequeno, oferece preencher
    if (cl && (!tec.bairros_cobertura || tec.bairros_cobertura.length === 0)) {
      if (confirm(`Preencher automaticamente os bairros de cobertura da unidade ${unidade}?`)) {
        preencherBairrosDaUnidade(cl.id);
      }
    }
  }

  async function recalcularCoef() {
    setRecalculando(true);
    try {
      const r = await tecnicos.recalcularCoeficiente(id);
      const novo = r.data;
      // Atualiza local
      if (novo.amostra >= 5) {
        setCampo('coeficiente_velocidade', novo.coeficiente);
      }
      setCampo('coeficiente_amostra', novo.amostra);
      setCampo('coeficiente_calculado_em', new Date().toISOString());
      // Re-fetch pra ter certeza
      await carregar();
    } catch (e) {
      alert('Erro: ' + (e.uiMessage || e.message));
    } finally { setRecalculando(false); }
  }

  async function desligar() {
    if (!confirm(`Desligar/desativar o técnico "${tec.apelido || tec.nome}"?\n\nA agenda dele não vai mais aparecer e ele não receberá novas atribuições. Histórico fica preservado.`)) return;
    setSalvando(true);
    try {
      await tecnicos.update(id, { ativo: false });
      navigate('/tecnicos');
    } catch (e) {
      alert('Erro: ' + (e.uiMessage || e.message));
    } finally { setSalvando(false); }
  }

  async function reativar() {
    setSalvando(true);
    try {
      await tecnicos.update(id, { ativo: true });
      setCampo('ativo', true);
    } catch (e) {
      alert('Erro: ' + (e.uiMessage || e.message));
    } finally { setSalvando(false); }
  }

  async function salvar(e) {
    e?.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      const payload = {
        nome: tec.nome,
        apelido: tec.apelido || null,
        telefone: tec.telefone || null,
        email: tec.email || null,
        cidade: tec.cidade || null,
        bairro: tec.bairro || null,
        endereco: tec.endereco || null,
        numero: tec.numero || null,
        complemento: tec.complemento || null,
        estado_uf: tec.estado_uf || null,
        cep: tec.cep || null,
        lat_residencia: tec.lat_residencia != null ? Number(tec.lat_residencia) : null,
        lon_residencia: tec.lon_residencia != null ? Number(tec.lon_residencia) : null,
        horario_inicio: tec.horario_inicio,
        horario_fim: tec.horario_fim,
        pausa_almoco_inicio: tec.pausa_almoco_inicio || null,
        pausa_almoco_fim: tec.pausa_almoco_fim || null,
        dias_trabalho: tec.dias_trabalho || [1,2,3,4,5],
        capacidade_diaria_os: Number(tec.capacidade_diaria_os) || 8,
        capacidade_diaria_minutos: Number(tec.capacidade_diaria_minutos) || 480,
        equipe_tamanho: Number(tec.equipe_tamanho) || 1,
        unidade_negocio: tec.atende_todas_unidades ? null : (tec.unidade_negocio || null),
        cluster_principal_id: tec.atende_todas_unidades ? null : (tec.cluster_principal_id ? Number(tec.cluster_principal_id) : null),
        atende_todas_unidades: !!tec.atende_todas_unidades,
        is_terceirizado: !!tec.is_terceirizado,
        foto_url: tec.foto_url || null,
        bairros_cobertura: tec.bairros_cobertura || [],
        ativo: !!tec.ativo,
        observacoes: tec.observacoes || null,
        horario_semanal: tec.horario_semanal || null,
        jornada_semanal_horas: tec.jornada_semanal_horas != null ? Number(tec.jornada_semanal_horas) : null,
      };
      await tecnicos.update(id, payload);

      const skillsPayload = [...skillsSelecionadas].map(tid => ({
        tipo_os_parametro_id: Number(tid),  // defesa final: garante number puro p/ Zod
        nivel: 3,
      }));
      await tecnicos.updateSkills(id, { skills: skillsPayload });

      setSucesso(true);
      setTimeout(() => setSucesso(false), 3000);
    } catch (e) {
      setErro(e.uiMessage || e.message || 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  const tiposAtivos = useMemo(() => tipos.filter(t => t.ativo_para_agendamento), [tipos]);
  const tiposInternos = useMemo(() => tipos.filter(t => !t.ativo_para_agendamento), [tipos]);

  if (carregando) return <div className="card">Carregando...</div>;
  if (!tec) return <div className="card error">{erro || 'Técnico não encontrado'}</div>;

  const coefDataFormatada = tec.coeficiente_calculado_em
    ? new Date(tec.coeficiente_calculado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

  return (
    <div className="stack">
      {erro && <div className="card error">{erro}</div>}
      {sucesso && (
        <div className="card" style={{ borderColor: '#16a34a', background: '#dcfce7', color: '#14532d', padding: '0.75rem 1rem' }}>
          ✓ Salvo com sucesso
        </div>
      )}

      {mostrarModalFoto && (
        <UploadFotoModal
          tecnicoId={tec.id}
          fotoAtual={tec.foto_url}
          onSalvo={(novaUrl) => {
            setCampo('foto_url', novaUrl);
            setMostrarModalFoto(false);
          }}
          onFechar={() => setMostrarModalFoto(false)}
        />
      )}

      <div className="row between">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar nome={tec.apelido || tec.nome} id={tec.id} fotoUrl={tec.foto_url} size={64}/>
          <div>
            <Link to="/tecnicos" className="muted">← Voltar</Link>
            <h2 style={{ margin: '0.3rem 0' }}>
              {tec.apelido || tec.nome}
              {!tec.ativo && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10 }}>INATIVO</span>}
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn small secondary" onClick={() => navigate('/tecnicos')} disabled={salvando}>
            Cancelar
          </button>
          <button className="btn small" onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : '💾 Salvar tudo'}
          </button>
        </div>
      </div>

      {/* ─── STATUS DO TÉCNICO (banner se inativo) ─── */}
      {!tec.ativo && (
        <div className="card" style={{ background: '#fef3c7', borderLeft: '4px solid #f59e0b', padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <strong>⚠ Técnico desligado/inativo</strong>
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                Não aparece em filtros padrão e não recebe novas visitas. Histórico preservado.
              </div>
            </div>
            <button className="btn small" onClick={reativar} disabled={salvando}>
              Reativar técnico
            </button>
          </div>
        </div>
      )}

      {/* ─── DADOS BÁSICOS ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Dados básicos</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <Campo label="Nome completo *" valor={tec.nome} onChange={v => setCampo('nome', v)} required />
          <Campo label="Apelido (exibido na agenda)" valor={tec.apelido} onChange={v => setCampo('apelido', v)} />
          <Campo label="Telefone" valor={tec.telefone} onChange={v => setCampo('telefone', v)} />
          <Campo label="E-mail" type="email" valor={tec.email} onChange={v => setCampo('email', v)} />
          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 500, display: 'block', marginBottom: 6 }}>
              Foto do técnico
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar nome={tec.apelido || tec.nome} id={tec.id} fotoUrl={tec.foto_url} size={56}/>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button type="button" className="btn small secondary"
                  onClick={() => setMostrarModalFoto(true)}>
                  📷 {tec.foto_url ? 'Trocar foto' : 'Enviar foto'}
                </button>
                <span className="muted" style={{ fontSize: '0.7rem' }}>
                  {tec.foto_url ? 'Clique no avatar pra ampliar' : 'Sem foto · usando iniciais'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', cursor: 'pointer' }}>
          <input type="checkbox"
            checked={!!tec.is_terceirizado}
            onChange={e => setCampo('is_terceirizado', e.target.checked)}
            style={{ width: 'auto' }}/>
          <strong>Técnico terceirizado / PJ</strong>
          <span className="muted" style={{ fontSize: '0.8rem' }}>(empresa parceira, não funcionário direto)</span>
        </label>
      </div>

      {/* ─── ENDEREÇO RESIDENCIAL ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>📍 Endereço residencial</h3>
        <p className="muted" style={{ marginTop: -4, marginBottom: 12, fontSize: '0.8rem' }}>
          Necessário pra aplicar feriado municipal correto e calcular saída de casa.
        </p>

        {/* CEP com botão de busca */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ flex: '0 0 160px' }}>
            <Campo label="CEP" valor={tec.cep || ''} onChange={v => setCampo('cep', v)} />
          </div>
          <button type="button" className="btn small secondary" style={{ marginBottom: 2 }}
            disabled={buscandoCep || !(tec.cep || '').replace(/\D/g, '').length === 8 && false}
            onClick={async () => {
              const cepLimpo = (tec.cep || '').replace(/\D/g, '');
              if (cepLimpo.length !== 8) { alert('CEP precisa ter 8 dígitos'); return; }
              setBuscandoCep(true);
              try {
                const r = await cepApi.buscar(cepLimpo);
                const d = r.data || {};
                if (d.logradouro)  setCampo('endereco',    d.logradouro);
                if (d.bairro)      setCampo('bairro',      d.bairro);
                if (d.cidade)      setCampo('cidade',      d.cidade);
                if (d.estado_uf)   setCampo('estado_uf',   d.estado_uf);
              } catch (e) {
                alert(e.uiMessage || 'CEP não encontrado');
              }
              setBuscandoCep(false);
            }}>
            {buscandoCep ? '⏳ Buscando…' : '🔍 Buscar endereço'}
          </button>
        </div>

        {/* Rua + número */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 12, marginBottom: 12 }}>
          <Campo label="Rua / Logradouro" valor={tec.endereco || ''} onChange={v => setCampo('endereco', v)} />
          <Campo label="Número" valor={tec.numero || ''} onChange={v => setCampo('numero', v)} />
        </div>

        {/* Complemento + Bairro */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
          <Campo label="Complemento" valor={tec.complemento || ''} onChange={v => setCampo('complemento', v)} />
          <Campo label="Bairro" valor={tec.bairro || ''} onChange={v => setCampo('bairro', v)} />
        </div>

        {/* Cidade + UF */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 12, marginBottom: 12 }}>
          <Campo label="Cidade" valor={tec.cidade || ''} onChange={v => setCampo('cidade', v)} />
          <Campo label="UF" valor={tec.estado_uf || ''} onChange={v => setCampo('estado_uf', (v || '').toUpperCase().slice(0,2))} />
        </div>

        {/* Coordenadas + botão geocodar */}
        <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6, marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Coordenadas (lat/lon)</strong>
            <button type="button" className="btn small primary"
              disabled={geocodando}
              onClick={async () => {
                // Salva antes de geocodar (geocode lê do banco)
                if (!confirm('Vou salvar o cadastro e em seguida buscar as coordenadas do endereço informado. Continuar?')) return;
                setGeocodando(true);
                try {
                  await salvar();  // persiste o endereço atual antes
                  const r = await geocode.tecnico(id);
                  setCampo('lat_residencia', r.data.lat);
                  setCampo('lon_residencia', r.data.lon);
                  alert(`✓ Coordenadas encontradas (qualidade: ${r.data.qualidade || 'n/d'})`);
                } catch (e) {
                  alert(e.uiMessage || 'Falha ao geocodar — confira o endereço');
                }
                setGeocodando(false);
              }}>
              {geocodando ? '⏳ Buscando…' : '🎯 Buscar coordenadas'}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Latitude" type="number" valor={tec.lat_residencia ?? ''} onChange={v => setCampo('lat_residencia', v === '' ? null : Number(v))} />
            <Campo label="Longitude" type="number" valor={tec.lon_residencia ?? ''} onChange={v => setCampo('lon_residencia', v === '' ? null : Number(v))} />
          </div>
          {tec.lat_residencia != null && tec.lon_residencia != null && (
            <div style={{ marginTop: 8 }}>
              <a href={`https://www.google.com/maps?q=${tec.lat_residencia},${tec.lon_residencia}`}
                 target="_blank" rel="noreferrer"
                 style={{ fontSize: 12, color: '#1e6fc8' }}>
                🗺️ Ver no Google Maps
              </a>
            </div>
          )}
          {tec.geocode_em && (
            <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
              Última busca: {new Date(tec.geocode_em).toLocaleString('pt-BR')} · status: {tec.geocode_status || '—'}
            </div>
          )}
        </div>
      </div>

      {/* ─── UNIDADE E COBERTURA ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Unidade e cobertura</h3>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
          <input type="checkbox"
            checked={!!tec.atende_todas_unidades}
            onChange={e => {
              setCampo('atende_todas_unidades', e.target.checked);
              if (e.target.checked) {
                setCampo('unidade_negocio', null);
                setCampo('cluster_principal_id', null);
              }
            }}
            style={{ width: 'auto' }}/>
          <strong>Atende todas as unidades</strong>
          <span className="muted" style={{ fontSize: '0.8rem' }}>(curinga — disponível em qualquer região)</span>
        </label>

        {!tec.atende_todas_unidades && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Unidade principal</label>
              <select
                value={tec.unidade_negocio || ''}
                onChange={e => onMudarUnidade(e.target.value)}
                style={{ width: '100%' }}>
                <option value="">(não definida)</option>
                {clustersList.map(c => (
                  <option key={c.id} value={c.unidade_negocio}>{c.unidade_negocio || c.nome}</option>
                ))}
              </select>
              {tec.cluster_principal_id && (
                <button type="button"
                  onClick={() => preencherBairrosDaUnidade(tec.cluster_principal_id)}
                  className="btn small secondary"
                  style={{ marginTop: '0.4rem', fontSize: '0.75rem' }}>
                  ⟲ Preencher bairros da unidade
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: '0.75rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            Bairros de cobertura ({(tec.bairros_cobertura || []).length})
          </label>
          <textarea
            value={(tec.bairros_cobertura || []).join(', ')}
            onChange={e => setCampo('bairros_cobertura',
              e.target.value.split(',').map(s => s.trim()).filter(Boolean)
            )}
            placeholder="Bairro 1, Bairro 2, Bairro 3..."
            style={{ width: '100%', minHeight: 80, fontFamily: 'inherit' }}/>
          <div className="muted" style={{ fontSize: '0.75rem' }}>
            Separe por vírgula. Use "⟲ Preencher bairros" pra auto-popular pela unidade.
          </div>
        </div>
      </div>
      {/* ─── HORÁRIO ─── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Horário de trabalho</h3>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: '#6b7280', marginRight: 6 }}>Modo:</span>
            <button type="button"
              className={`btn small ${!tec.horario_semanal ? 'primary' : 'secondary'}`}
              onClick={() => setCampo('horario_semanal', null)}>
              Simples
            </button>
            <button type="button"
              className={`btn small ${tec.horario_semanal ? 'primary' : 'secondary'}`}
              onClick={() => {
                // Inicializa com horário atual nos dias_trabalho atuais
                const inicio = (tec.horario_inicio || '08:00').slice(0,5);
                const fim    = (tec.horario_fim    || '18:00').slice(0,5);
                const dias = tec.dias_trabalho || [1,2,3,4,5];
                const novo = {};
                for (const d of dias) {
                  // mapear 7 → 0 (domingo no schema antigo era 7, no novo é 0)
                  const k = d === 7 ? 0 : d;
                  novo[k] = { inicio, fim, almoco_min: 60 };
                }
                setCampo('horario_semanal', novo);
              }}>
              Personalizado por dia
            </button>
          </div>
        </div>

        {!tec.horario_semanal ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
              <Campo label="Início *" type="time" valor={(tec.horario_inicio||'').slice(0,5)} onChange={v => setCampo('horario_inicio', v)} />
              <Campo label="Fim *" type="time" valor={(tec.horario_fim||'').slice(0,5)} onChange={v => setCampo('horario_fim', v)} />
              <Campo label="Almoço início" type="time" valor={(tec.pausa_almoco_inicio||'').slice(0,5)} onChange={v => setCampo('pausa_almoco_inicio', v || null)} />
              <Campo label="Almoço fim" type="time" valor={(tec.pausa_almoco_fim||'').slice(0,5)} onChange={v => setCampo('pausa_almoco_fim', v || null)} />
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.3rem', display: 'block' }}>
                Dias da semana
              </label>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {DIAS_VALUES.map((d, i) => {
                  const ativo = (tec.dias_trabalho || []).includes(d);
                  return (
                    <button
                      key={d} type="button"
                      onClick={() => toggleDia(d)}
                      style={{
                        padding: '0.4rem 0.8rem',
                        borderRadius: 6,
                        border: `1px solid ${ativo ? '#1e6fc8' : 'var(--cinza-borda)'}`,
                        background: ativo ? '#1e6fc8' : '#fff',
                        color: ativo ? '#fff' : '#374151',
                        cursor: 'pointer',
                        fontWeight: 500, fontSize: '0.85rem',
                      }}>
                      {DIAS_NOMES[i]}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
              <span>Meta semanal:</span>
              {[44, 40, 36].map(m => (
                <button key={m} type="button"
                  className={`btn small ${Number(tec.jornada_semanal_horas) === m ? 'primary' : 'secondary'}`}
                  onClick={() => setCampo('jornada_semanal_horas', m)}>
                  {m}h
                </button>
              ))}
              <input type="number" min="20" max="60" step="0.5"
                placeholder="outra"
                value={tec.jornada_semanal_horas != null && ![44,40,36].includes(Number(tec.jornada_semanal_horas)) ? tec.jornada_semanal_horas : ''}
                onChange={e => setCampo('jornada_semanal_horas', e.target.value === '' ? null : Number(e.target.value))}
                style={{ width: 70, padding: '4px 8px' }} />
            </div>
            <HorarioSemanalEditor
              horario={tec.horario_semanal}
              metaHoras={Number(tec.jornada_semanal_horas) || 44}
              onChange={v => setCampo('horario_semanal', v)}
            />
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
              💡 No modo personalizado, "Início/Fim/Almoço/Dias" simples são ignorados — vale o que estiver na tabela acima.
              Para voltar ao modo simples, clique em "Simples" e os dados acima serão descartados.
            </p>
          </>
        )}
      </div>

      {/* ─── {/* ─── CAPACIDADE ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Capacidade e equipe</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <Campo
            label="OS/dia (limite)"
            type="number"
            valor={tec.capacidade_diaria_os}
            onChange={v => setCampo('capacidade_diaria_os', v)}
            min={1} max={30}/>
          <Campo
            label="Minutos úteis/dia"
            type="number"
            valor={tec.capacidade_diaria_minutos}
            onChange={v => setCampo('capacidade_diaria_minutos', v)}
            min={60} max={1440}/>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Equipe</label>
            <select value={tec.equipe_tamanho || 1} onChange={e => setCampo('equipe_tamanho', Number(e.target.value))} style={{ width: '100%' }}>
              <option value={1}>Solo (1 técnico)</option>
              <option value={2}>Dupla (2 técnicos)</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── COEFICIENTE (READ-ONLY) ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Coeficiente de velocidade <span className="muted" style={{ fontWeight: 'normal', fontSize: '0.8rem' }}>(calculado automaticamente)</span></h3>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div className="muted" style={{ fontSize: '0.75rem' }}>Coef. atual</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {Number(tec.coeficiente_velocidade).toFixed(2)}x
            </div>
            <div className="muted" style={{ fontSize: '0.7rem' }}>
              {tec.coeficiente_velocidade < 0.9 ? '⚡ mais rápido que a média' :
                tec.coeficiente_velocidade > 1.1 ? '🐢 mais lento que a média' : '🎯 média'}
            </div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: '0.75rem' }}>Amostra (30d)</div>
            <div style={{ fontSize: '1.2rem' }}>
              {tec.coeficiente_amostra ?? '-'} OS finalizadas
            </div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: '0.75rem' }}>Calculado em</div>
            <div style={{ fontSize: '0.85rem' }}>{coefDataFormatada}</div>
          </div>
          <button className="btn small secondary" onClick={recalcularCoef} disabled={recalculando}>
            {recalculando ? 'Recalculando...' : '⟲ Recalcular agora'}
          </button>
        </div>
        <div className="muted" style={{ fontSize: '0.75rem', marginTop: '0.75rem' }}>
          Coef. = mediana(tempo_real / p75_do_tipo) nas últimas OS dos 30 dias.
          {' '}{tec.coeficiente_amostra < 5 && 'Amostra muito pequena — mantém valor antigo até ter dados suficientes.'}
        </div>
      </div>

      {/* ─── SKILLS ─── */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0 }}>Skills (tipos de OS que atende)</h3>
            <div className="muted" style={{ fontSize: '0.8rem' }}>
              {skillsSelecionadas.size} de {tiposAtivos.length} tipos selecionados
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="btn small secondary" onClick={marcarTodasSkills}>
              Marcar todas
            </button>
            <button type="button" className="btn small secondary" onClick={desmarcarTodasSkills}>
              Desmarcar todas
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.4rem' }}>
          {tiposAtivos.map(t => {
            const marcada = skillsSelecionadas.has(Number(t.id));
            return (
              <label key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 0.7rem', borderRadius: 6,
                background: marcada ? '#dbeafe' : '#f9fafb',
                border: `1px solid ${marcada ? '#3b82f6' : 'var(--cinza-borda)'}`,
                cursor: 'pointer', fontSize: '0.8rem',
              }}>
                <input type="checkbox" checked={marcada} onChange={() => toggleSkill(t.id)} style={{ width: 'auto', margin: 0 }}/>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: marcada ? 600 : 400 }}>{t.tipo}</div>
                  <div className="muted" style={{ fontSize: '0.7rem' }}>
                    P75: {t.exec_p75_min || '?'} min · janela {t.janela_recomendada_min || '?'} min
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {tiposInternos.length > 0 && (
          <details style={{ marginTop: '0.75rem' }}>
            <summary className="muted" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
              Mostrar {tiposInternos.length} tipos operacionais/internos
            </summary>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.4rem', marginTop: '0.5rem' }}>
              {tiposInternos.map(t => {
                const marcada = skillsSelecionadas.has(Number(t.id));
                return (
                  <label key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.4rem 0.6rem', borderRadius: 6,
                    background: marcada ? '#dbeafe' : '#f9fafb',
                    border: `1px solid ${marcada ? '#3b82f6' : 'var(--cinza-borda)'}`,
                    cursor: 'pointer', fontSize: '0.75rem',
                  }}>
                    <input type="checkbox" checked={marcada} onChange={() => toggleSkill(t.id)} style={{ width: 'auto', margin: 0 }}/>
                    {t.tipo}
                  </label>
                );
              })}
            </div>
          </details>
        )}
      </div>

      {/* ─── OBSERVAÇÕES ─── */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Observações</h3>
        <textarea
          value={tec.observacoes || ''}
          onChange={e => setCampo('observacoes', e.target.value)}
          placeholder="Notas internas sobre esse técnico..."
          style={{ width: '100%', minHeight: 80, fontFamily: 'inherit' }}/>
      </div>

      {/* ─── ZONA DE DESLIGAMENTO ─── */}
      {tec.ativo && (
        <div className="card" style={{ borderColor: '#fca5a5', background: '#fff5f5' }}>
          <h3 style={{ marginTop: 0, color: '#991b1b' }}>Zona de desligamento</h3>
          <p style={{ margin: '0.5rem 0', fontSize: '0.85rem' }}>
            Desligar o técnico mantém o histórico (OS, agendas passadas) mas:
          </p>
          <ul style={{ margin: '0.3rem 0 0.75rem 1.5rem', fontSize: '0.85rem' }}>
            <li>Não aparece nos filtros padrão da Agenda e Mapa</li>
            <li>Não recebe novas atribuições</li>
            <li>Não conta no motor de disponibilidade</li>
          </ul>
          <button className="btn" onClick={desligar} disabled={salvando}
            style={{ background: '#dc2626' }}>
            🚫 Desligar este técnico
          </button>
        </div>
      )}

      {/* ─── BOTÃO FIXO ─── */}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem 0' }}>
        <button className="btn secondary" onClick={() => navigate('/tecnicos')} disabled={salvando}>
          Cancelar
        </button>
        <button className="btn" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : '💾 Salvar tudo'}
        </button>
      </div>
    </div>
  );
}

function Campo({ label, valor, onChange, type = 'text', required, min, max, step }) {
  return (
    <div>
      <label style={{ fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>{label}</label>
      <input
        type={type}
        value={valor ?? ''}
        onChange={e => onChange(e.target.value)}
        required={required}
        min={min} max={max} step={step}
        style={{ width: '100%' }}/>
    </div>
  );
}
